import { type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./dialog";
import { Button } from "./button";

export interface AdminActionDialogProps {
  open: boolean;
  /** Called with `false` when the user dismisses. Ignored while `isPending`. */
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** What the action does and what it cannot undo. Shown above the fields. */
  description?: ReactNode;
  /** Action-specific inputs — a reason box, a status picker, whatever the action needs. */
  children?: ReactNode;
  confirmLabel: string;
  /** Shown on the confirm button while the request is in flight. */
  pendingLabel?: string;
  confirmVariant?: "default" | "destructive";
  /** Blocks confirm for reasons the dialog cannot know, e.g. a required reason left blank. */
  confirmDisabled?: boolean;
  isPending?: boolean;
  /** Server-side failure. Rendered in place; the dialog stays open so the input survives. */
  error?: string | null;
  onConfirm: () => void;
}

/**
 * The shared shell for a staff action taken against one row.
 *
 * Owns only what every such action needs identically — chrome, the pending state, the
 * error banner, and a footer whose buttons cannot be double-fired. The fields belong to
 * the caller, because a suspension reason and a verification decision have nothing in
 * common beyond sitting in a dialog.
 *
 * Two behaviours here are deliberate and easy to lose when this is written per-screen:
 * a failed action leaves the dialog open with its input intact, so the operator can read
 * the reason and retry rather than re-typing; and dismissal is refused while a request
 * is in flight, so a half-finished action cannot be abandoned into an unknown state.
 */
export function AdminActionDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  pendingLabel,
  confirmVariant = "default",
  confirmDisabled = false,
  isPending = false,
  error,
  onConfirm
}: AdminActionDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing mid-flight would strand the operator: the request still lands, but
        // they never learn whether it did.
        if (!next && isPending) return;
        onOpenChange(next);
      }}
    >
      {/*
        Capped at the viewport with the body scrolling inside it. DialogContent is
        centred with `translate-y-[-50%]` and has no height limit of its own, so a form
        taller than the window grows off both edges at once -- the top is unreachable and
        there is nothing to scroll. The pricing-plan form is the one that hits it first;
        every dialog built on this shell would have hit it eventually.

        Header and footer stay put so the title and the confirm button are always visible;
        only the fields move.
      */}
      <DialogContent className="max-h-[85dvh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription asChild>
              <div className="space-y-3 pt-1">{description}</div>
            </DialogDescription>
          ) : null}
        </DialogHeader>

        {children ? (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3 py-1">{children}</div>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="shrink-0 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending || confirmDisabled}
          >
            {isPending ? (pendingLabel ?? `${confirmLabel}…`) : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
